import React, { useEffect, useState, useRef } from 'react';
import { Menu, X, ArrowRight, CheckCircle, Eye, TrendingUp, Cpu, Users, Code, Layout, BarChart2, Mail, Instagram } from 'lucide-react';
import { FaTiktok } from 'react-icons/fa';

// --- Global Data ---
const services = [
    {
      id: 'dev',
      title: 'Website Design and Development',
      description: 'At Vyxion Labs, we build websites tailored to your business — because no two companies, industries, or goals are the same. We take the time to understand your needs and customise every aspect of your site, from structure to functionality. With clean, seamless interfaces and conversion-focused design, we create experiences that guide users to take action. Beyond design and development, we handle every detail of your website end-to-end, so it doesn’t just look good — it delivers real, measurable business results.',
      features: ['Custom Web Designs', 'Responsive Design', 'Security'],
      image: '/web_design.jpg',
      gradient: 'from-cyan-400 to-blue-500', 
      cursorColor: 'rgba(34, 211, 238, 0.4)',
      hoverCursorColor: 'rgba(59, 130, 246, 0.6)',
      link: '/web-development',
    },
    {
      id: 'revamp',
      title: 'Website Revamp',
      description: 'Are you looking to give your website a fresh new look? Discover the benefits of our professional Website Revamp service. From enhanced functionality to improved user experience, we help you maximize your conversions.',
      features: ['Modern UI/UX Design', 'Performance Boost', 'Mobile Optimization', 'Brand Refresh'],
      image: '/web_revamp.jpg',
      gradient: 'from-purple-400 to-pink-500', 
      cursorColor: 'rgba(168, 85, 247, 0.4)',
      hoverCursorColor: 'rgba(236, 72, 153, 0.6)',
      link: '/website-revamp',
    },
    {
      id: 'hosting',
      title: 'Server Hosting & Maintenance',
      description: 'Reliable, secure hosting solutions with 24/7 monitoring and expert maintenance to keep your site running flawlessly.',
      features: ['24/7 Server Monitoring', 'Security Updates', 'Content Revision', 'Technical Support'],
      image: '/server_hosting.jpg',
      gradient: 'from-emerald-400 to-teal-500',
      cursorColor: 'rgba(52, 211, 153, 0.4)',
      hoverCursorColor: 'rgba(20, 184, 166, 0.6)',
      link: '/hosting-maintenance',
    },
  ];


// --- CTABannerSection ---
const CTABannerSection = ({ visible }) => {
    return (
        <section className="relative py-16 md:py-20 px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
                <div
                    id="cta-banner"
                    data-observe
                    className={`flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-0 transition-all duration-1000 ${
                        visible['cta-banner'] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-20"
                    }`}
                >
                    <div className="lg:w-1/2 text-center lg:text-left space-y-4">
                        <p className="text-lg md:text-xl font-medium text-slate-700">Got an idea you’re excited about?</p>
                        <h2 className="text-3xl md:text-6xl font-black leading-tight">
                            Let’s bring it to life. <br />
                            <span className="font-serif italic text-cyan-600">All it takes is a conversation!</span>
                        </h2>

                        <a href="/consult" className="inline-flex items-center gap-2 px-8 py-3 bg-cyan-500 text-white font-bold rounded-xl shadow-lg hover:bg-cyan-600 transition-colors mt-4">
                            Start the Conversation <ArrowRight className="w-5 h-5" />
                        </a>
                    </div>

                    <div className="lg:w-1/2 text-center lg:text-right space-y-3 font-semibold">
                        <div className="text-2xl md:text-4xl font-extrabold text-slate-900">
                            For enquiries, email us:
                        </div>
                        <div className="text-lg md:text-2xl text-slate-700 break-all">
                        vyxionlabs.info@gmail.com
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

// --- WhyChooseUsSection ---
const WhyChooseUsSection = ({ visible }) => {
    const benefits = [
        {
            icon: Cpu,
            title: 'Customized Solutions',
            description: 'We offer tailor-made website solutions that address your specific goals and operational requirements.',
            gradient: 'from-cyan-500 to-blue-600',
        },
        {
            icon: Eye,
            title: 'Full Transparency',
            description: 'We ensure complete clarity on timelines, pricing, and project progress, building trust from consultation to launch.',
            gradient: 'from-slate-600 to-slate-800',
        },
        {
            icon: TrendingUp,
            title: 'Clear Communication',
            description: 'We listen closely to your needs to ensure the final product exceeds your expectations through continuous feedback.',
            gradient: 'from-indigo-500 to-purple-600',
        },
        {
            icon: Users,
            title: 'Dedicated Support',
            description: 'We offer reliable post-launch support and maintenance to ensure your online presence runs smoothly.',
            gradient: 'from-teal-500 to-cyan-600',
        },
    ];

    return (
        <section className="relative py-16 lg:py-20 px-4 lg:px-6">
            <div className="max-w-7xl mx-auto">
                <div 
                    id="why-choose-us"
                    data-observe
                    className={`flex flex-col lg:flex-row rounded-3xl overflow-hidden 
                    bg-white/70 backdrop-blur-xl 
                    border border-slate-200/60 
                    shadow-xl 
                    p-6 lg:p-12 
                    transition-all duration-1000 
                    ${visible['why-choose-us'] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-20"}`}
                >
                    {/* Left Block */}
                    <div className="lg:w-1/3 p-2 lg:p-4 flex flex-col justify-between space-y-6 lg:space-y-8 lg:pr-12 mb-8 lg:mb-0">
                        <div className="space-y-4 text-center lg:text-left">
                            <h2 className="text-3xl md:text-5xl font-black leading-tight text-slate-900">
                                Why Choose Vyxion Labs?
                            </h2>
                            <p className="text-base lg:text-lg text-slate-600">
                                Experience the difference our structured process and commitment to quality bring to your digital presence.
                            </p>
                        </div>

                        <a 
                            href="/consult"
                            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 
                            px-6 py-3 
                            bg-slate-900 text-white 
                            font-semibold 
                            rounded-xl 
                            shadow-lg 
                            hover:bg-slate-800 
                            transition-colors"
                        >
                            Start Your Project <ArrowRight className="w-5 h-5" />
                        </a>
                    </div>

                    {/* Right Block */}
                    <div className="lg:w-2/3 p-2 lg:p-4 space-y-6 lg:border-l lg:border-slate-200 lg:pl-12">
                        {benefits.map((b, index) => (
                            <div 
                                key={index} 
                                className={`flex items-start gap-4 ${
                                    index < benefits.length - 1 
                                    ? 'pb-6 border-b border-slate-200/80' 
                                    : ''
                                }`}
                            >
                                <div className={`w-12 h-12 flex-shrink-0 flex items-center justify-center 
                                rounded-xl 
                                bg-gradient-to-br ${b.gradient} 
                                shadow-md`}>
                                    <b.icon className="w-6 h-6 text-white" />
                                </div>
                                
                                <div>
                                    <h3 className="text-lg lg:text-xl font-bold text-slate-900 mb-1">
                                        {b.title}
                                    </h3>
                                    <p className="text-slate-600 text-sm leading-relaxed">
                                        {b.description}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>

                </div>
            </div>
        </section>
    );
};



// --- FooterSection ---
const FooterSection = () => {
    return (
        <footer className="bg-slate-900/90 backdrop-blur-sm text-slate-300 border-t border-slate-700/50 py-12 md:py-16 px-6">
            <div className="max-w-7xl mx-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-8 md:gap-12 border-b border-slate-700 pb-12">
                    {/* Brand & Contact */}
                    <div className="sm:col-span-2 space-y-6">
                        <a href="/" className="flex items-center gap-3">
                            <img src="/logo.png" alt="SR logo" className="w-12 h-12 object-cover rounded-xl" />
                            <div>
                                <div className="text-xl font-bold text-white">Vyxion Labs</div>
                                <div className="text-sm text-slate-400">Digital Presence. Engineered.</div>
                            </div>
                        </a>
                        <p className="text-sm text-slate-400 max-w-xs">
                            We are committed to delivering outstanding digital solutions that combine stunning design with robust performance.
                        </p>
                        <div className="flex space-x-4 pt-2">
                            <a href="https://www.tiktok.com/@vyxion.labs" className="text-slate-400 hover:text-cyan-500 transition-colors" aria-label="TikTok"><FaTiktok size={20} /></a>
                            <a href="https://www.instagram.com/vyxionlabs" className="text-slate-400 hover:text-cyan-500 transition-colors" aria-label="Instagram"><Instagram size={20} /></a>
                        </div>
                    </div>

                    {/* Quick Links */}
                    <div className="space-y-4">
                        <h4 className="text-lg font-bold text-white mb-4">Quick Links</h4>
                        <ul className="space-y-3">
                            <li><a href="/consult" className="text-sm hover:text-cyan-500 transition-colors">Book a Free Consult</a></li>
                            <li><a href="/pricing" className="text-sm hover:text-cyan-500 transition-colors">Pricing</a></li>
                            <li><a href="/ourworks" className="text-sm hover:text-cyan-500 transition-colors">Our Works</a></li>
                            <li><a href="/faq" className="text-sm hover:text-cyan-500 transition-colors">FAQ</a></li>
                            <li><a href="/about" className="text-sm hover:text-cyan-500 transition-colors">About Us</a></li>
                        </ul>
                    </div>

                    {/* Services */}
                    <div className="space-y-4">
                        <h4 className="text-lg font-bold text-white mb-4">Services</h4>
                        <ul className="space-y-3">
                            {services.map((s, index) => (
                                <li key={index}><a href={s.link} className="text-sm hover:text-cyan-500 transition-colors">{s.title}</a></li>
                            ))}
                        </ul>
                    </div>
                    
                    {/* Contact */}
                    <div className="space-y-4 col-span-1">
                        <h4 className="text-lg font-bold text-white mb-4">Contact</h4>
                        <ul className="space-y-3">
                            <li className="flex items-center gap-3">
                                <Mail size={16} className="text-cyan-500 flex-shrink-0" />
                                <a href="mailto:vyxionlabs.info@gmail.com" className="text-sm hover:text-cyan-500 transition-colors break-all">vyxionlabs.info@gmail.com</a>
                            </li>
                        </ul>
                    </div>
                </div>
                
                {/* Legal Links */}
                <div className="flex flex-col md:flex-row justify-center md:justify-start gap-4 md:space-x-6 mt-8 items-center md:items-start">
                   
                    <a href="/privacy" className="text-xs text-slate-500 hover:text-cyan-500 transition-colors font-medium">Privacy Policy</a>
                </div>

                <div className="mt-4 text-center text-sm text-slate-500">
                    &copy; {new Date().getFullYear()} Vyxion Labs. All rights reserved.
                </div>
            </div>
        </footer>
    );
};

export default function WebsiteRevampPage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const [isContactPopupOpen, setIsContactPopupOpen] = useState(false);
  
  const [visible, setVisible] = useState({});
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });
  const [hoveredService, setHoveredService] = useState(null);
  const [isHoveringButton, setIsHoveringButton] = useState(false);
  const [cardRect, setCardRect] = useState(null); 
  const [cursorScale, setCursorScale] = useState(1); 
  
  const servicesDropdownRef = useRef(null); 
  // Ref to detect click outside mobile menu
  const mobileMenuRef = useRef(null);

  const handleServicesClick = (e) => {
    e.stopPropagation(); 
    setIsServicesDropdownOpen(!isServicesDropdownOpen);
  };
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (servicesDropdownRef.current && !servicesDropdownRef.current.contains(event.target)) {
        setIsServicesDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [servicesDropdownRef]);

  const handleMenuClick = () => {
    setIsContactPopupOpen(!isContactPopupOpen);
    setMenuOpen(false); // Close generic mobile menu if open
  };

  // --- Custom Cursor Logic ---
  const revampServiceIndex = 1; 

  useEffect(() => {
    // Only enable custom cursor logic on non-touch devices for performance
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (isTouchDevice) return;

    const handleMouseMove = (e) => {
      setCursorPos({ x: e.clientX, y: e.clientY });

      if (cardRect && hoveredService === revampServiceIndex) {
        const x = e.clientX;
        const y = e.clientY;

        const centerX = cardRect.left + cardRect.width / 2;
        const centerY = cardRect.top + cardRect.height / 2;

        const distX = Math.abs(x - centerX);
        const distY = Math.abs(y - centerY);

        const normDistX = distX / (cardRect.width / 2);
        const normDistY = distY / (cardRect.height / 2);

        const maxNormDist = Math.max(normDistX, normDistY);
        
        const maxScale = 1.5; 
        const minScale = 1;   
        
        const proximity = 1 - Math.min(1, maxNormDist);
        
        const newScale = minScale + (maxScale - minScale) * proximity;
        setCursorScale(newScale);

      } else if (cursorScale !== 1) {
          setCursorScale(1); 
      }
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [cardRect, hoveredService, cursorScale]); 


  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setVisible((v) => ({ ...v, [e.target.id]: true }));
        });
      },
      { threshold: 0.1 } // Lower threshold for mobile responsiveness
    );

    document.querySelectorAll('[data-observe]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const revampService = services[revampServiceIndex];
  
  return (
    
    <div className="bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 min-h-screen overflow-x-hidden">
      <style>{`
        @keyframes float { 
          0%, 100% { transform: translateY(0px) rotate(0deg); } 
          50% { transform: translateY(-20px) rotate(5deg); } 
        }
        @keyframes pulse-glow { 
          0%, 100% { opacity: 0.5; transform: scale(1); } 
          50% { opacity: 1; transform: scale(1.05); } 
        }
        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }
        @keyframes slideInUp {
            0% { transform: translateY(50px); opacity: 0; }
            100% { transform: translateY(0); opacity: 1; }
        }
        /* Mobile menu styles */
        .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.4);
            backdrop-filter: blur(8px);
            z-index: 9999; 
            opacity: 0;
            visibility: hidden;
            transition: opacity 0.3s ease, visibility 0.3s ease;
        }
        .modal-overlay.open {
            opacity: 1;
            visibility: visible;
        }
        .modal-content {
            position: fixed;
            top: 0;
            right: 0;
            width: 85%;
            max-width: 400px;
            height: 100%;
            background: rgba(255, 255, 255, 0.98);
            backdrop-filter: blur(30px);
            box-shadow: -10px 0 30px rgba(0, 0, 0, 0.2);
            transform: translateX(100%);
            transition: transform 0.4s cubic-bezier(0.86, 0, 0.07, 1);
            overflow-y: auto;
            padding: 2rem 1.5rem;
        }
        .modal-overlay.open .modal-content {
            transform: translateX(0);
        }

        .float-animation { animation: float 6s ease-in-out infinite; }
        .pulse-glow { animation: pulse-glow 3s ease-in-out infinite; }
        .shimmer {
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.8), transparent);
          background-size: 1000px 100%;
          animation: shimmer 3s infinite;
        }
        .glass {
          background: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.5);
        }
        
        /* Hide custom cursor on touch devices */
        @media (hover: none) and (pointer: coarse) {
            .custom-cursor { display: none !important; }
        }
        .custom-cursor {
          pointer-events: none;
          position: fixed;
          width: 60px;
          height: 60px;
          border-radius: 50%;
          transform: translate(-50%, -50%);
          transition: all 0.15s ease-out; 
          z-index: 9999;
          mix-blend-mode: multiply;
        }
        .animate-slideInUp { animation: slideInUp 1s ease-out forwards; }
      `}</style>

      {/* Custom Cursor */}
      {hoveredService === revampServiceIndex && (
        <div
          className="custom-cursor"
          style={{
            left: `${cursorPos.x}px`,
            top: `${cursorPos.y}px`,
            backgroundColor: isHoveringButton 
              ? revampService.hoverCursorColor 
              : revampService.cursorColor,
            transform: `translate(-50%, -50%) scale(${cursorScale * (isHoveringButton ? 1.3 : 1)})`,
            width: '60px',
            height: '60px',
          }}
        />
      )}
      
      

      
      <header className="w-full z-[100] border-b border-neutral-200 bg-white sticky top-0">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <a href="/" className="flex items-center gap-3 z-50 opacity-100 fill-mode-forwards flex-shrink-0">
              <div className="relative w-10 h-10 md:w-14 md:h-14 flex items-center justify-center">
                <img src="/logo.png" alt="SR logo" className="w-full h-full object-cover rounded-sm" />
              </div>
              <div>
                <div className="text-lg font-bold text-neutral-900">Vyxion Labs</div>
                <div className="text-[10px] md:text-xs text-neutral-600 font-medium">Digital Presence. Engineered.</div>
              </div>
            </a>

            {/* Nav - Center - Flexbox Layout */}
            <nav className="hidden lg:flex flex-1 justify-center items-center gap-6 xl:gap-8  opacity-100 fill-mode-forwards px-4" style={{animationFillMode: 'forwards'}}>
                <a href="/" className="text-neutral-700 hover:text-cyan-600 transition-colors font-medium">Home</a>
                <div className="relative" ref={servicesDropdownRef}>
                    <span id="services-link" className="text-neutral-900 font-bold cursor-pointer" onClick={handleServicesClick}>Services</span>
                    {isServicesDropdownOpen && (
                   <div id="services-dropdown" className="absolute left-1/2 transform -translate-x-1/2 mt-2 w-64 bg-white rounded-sm border border-neutral-200 overflow-hidden z-[110]">
                   <div className="flex flex-col p-2">
                       <a href="/web-development" onClick={() => setIsServicesDropdownOpen(false)} className="p-3 block rounded-sm hover:bg-neutral-100 transition-colors text-sm font-medium text-neutral-800">
                           Website Design & Development
                       </a>
                       <a href="/website-revamp" onClick={() => setIsServicesDropdownOpen(false)} className="p-3 block rounded-sm bg-neutral-100 transition-colors text-sm font-bold text-neutral-900">
                           Website Revamp
                       </a>
                       <a href="/hosting-maintenance" onClick={() => setIsServicesDropdownOpen(false)} className="p-3 block rounded-sm hover:bg-neutral-100 transition-colors text-sm font-medium text-neutral-800">
                           Server Hosting & Maintenance
                       </a>
                   </div>
               </div>
               
                    )}
                </div>
                <a href="/pricing" className="text-neutral-700 hover:text-cyan-600 transition-colors font-medium">Pricing</a>
                <a href="/about" className="text-neutral-700 hover:text-cyan-600 transition-colors font-medium">About Us</a>
                <a href="/ourworks" className="text-neutral-700 hover:text-cyan-500 transition-colors font-medium">Our Works</a>
                <a href="/faq" className="text-neutral-700 hover:text-cyan-600 transition-colors font-medium">FAQ</a>
            </nav>

            {/* Right Controls */}
            <div className="flex items-center gap-3 md:gap-4 opacity-100 fill-mode-forwards flex-shrink-0" style={{animationFillMode: 'forwards'}}>
                <a href="/consult" className="hidden lg:flex group relative px-5 py-2 bg-gray-800 text-gray-200 font-semibold rounded-md hover:bg-gray-700 transition-all text-sm shadow-sm">
                  <span className="relative z-10 flex items-center gap-2 whitespace-nowrap">Free Consult <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></span>
                </a>
                <div className="flex items-center gap-3 border-l border-neutral-200 pl-3 md:pl-4">
                    <a href="https://www.tiktok.com/@vyxion.labs" target="_blank" rel="noopener noreferrer" className="text-neutral-600 hover:text-black transition-colors"><FaTiktok className="w-4 h-4" /></a>
                    <a href="https://www.instagram.com/vyxionlabs" target="_blank" rel="noopener noreferrer" className="text-neutral-600 hover:text-pink-600 transition-colors"><Instagram className="w-4 h-4" /></a>
                </div>
                <button className="lg:hidden flex items-center gap-1 px-3 py-2 text-neutral-700 font-semibold rounded-sm border border-neutral-300 hover:bg-neutral-100 transition-all text-sm bg-white ml-2" onClick={handleMenuClick}>
                    <Menu className="w-5 h-5" />
                </button>
            </div>
          </div>
        </div>
      </header>
      
      {/* Contact Overlay */}
      <div className={`modal-overlay ${isContactPopupOpen ? 'open' : ''}`} onClick={handleMenuClick}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="absolute top-6 right-6 p-2 rounded-sm bg-neutral-100 hover:bg-neutral-200 transition-colors" onClick={handleMenuClick}>
                <X size={24} className="text-neutral-700" />
            </button>
            <h2 className="text-3xl font-bold text-neutral-900 mb-8 mt-12 animate-slideInLeft">Navigation</h2>
            <div className="space-y-4 mb-10 border-b border-neutral-200 pb-8">
                <div className="space-y-3 pl-4 border-l-2 border-purple-200 animate-slideInLeft delay-100">
                     <p className="text-xs font-bold text-neutral-400 uppercase tracking-wider">Services</p>
                     <a href="/web-development" className="block text-base font-medium text-neutral-600 hover:text-cyan-500" onClick={handleMenuClick}>Web Development</a>
                     <a href="/website-revamp" className="block text-base font-medium text-neutral-600 hover:text-cyan-500" onClick={handleMenuClick}>Website Revamp</a>
                     <a href="/hosting-maintenance" className="block text-base font-medium text-neutral-600 hover:text-cyan-500" onClick={handleMenuClick}>Hosting</a>
                </div>
                <a href="/pricing" className="block text-2xl font-bold text-neutral-800 hover:text-cyan-500 animate-slideInLeft delay-200" onClick={handleMenuClick}>Pricing</a>
                <a href="/" className="block text-2xl font-bold text-neutral-800 hover:text-cyan-500 animate-slideInLeft delay-200" onClick={handleMenuClick}>Home</a>
                <a href="/faq" className="block text-2xl font-bold text-neutral-800 hover:text-cyan-500 animate-slideInLeft delay-300" onClick={handleMenuClick}>FAQ</a>
                <a href="/about" className="block text-2xl font-bold text-neutral-800 hover:text-cyan-500 animate-slideInLeft delay-400" onClick={handleMenuClick}>About Us</a>
                <a href="/consult" className="group relative block w-full text-center mt-12 px-6 py-4 bg-gray-800 text-gray-200 font-bold rounded-md hover:bg-gray-700 transition-all text-sm" onClick={handleMenuClick}>
              <span className="relative z-10 flex items-center justify-center gap-2">Start a Project Today <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" /></span>
            </a>
            </div>
            <div>
                
            </div>
            <h3 className="text-2xl font-bold text-neutral-900 mb-4">Get in Touch</h3>
            <div className="space-y-4 mb-10">
                <div className="flex items-start gap-4 p-4 rounded-sm border border-neutral-200 bg-neutral-50">
                    <Mail size={24} className="text-cyan-600 flex-shrink-0 mt-0.5" />
                    <div>
                        <div className="text-lg font-bold text-neutral-800">Email</div>
                        <a href="mailto:vyxionlabs.info@gmail.com" className="text-neutral-600 hover:text-cyan-500 transition-colors text-base break-all">vyxionlabs.info@gmail.com</a>
                    </div>
                </div>
            </div>
            
        </div>
      </div>

      <main>
      <div className="bg-white text-neutral-900 min-h-screen antialiased relative overflow-x-hidden font-inherit">
        <section className="relative py-12 lg:py-16 px-6">
            <div className="max-w-7xl mx-auto mt-4 lg:mt-0">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black leading-tight lg:leading-none">
                <span className="block bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 bg-clip-text text-transparent drop-shadow-2xl">
                Website Revamp <br className="hidden md:block"/>& Redesign
                </span>
            </h1>
            <p className="text-lg lg:text-xl text-slate-600 mt-4 lg:mt-6 max-w-2xl text-left">
                Revitalize your online presence with a modern, fast, and conversion-optimized website revamp.
            </p>
            </div>
        </section>
    

        {/* Dedicated Service Section - Revamp Image and Text */}
        <section className="relative pt-0 pb-16 lg:pb-20 px-4 lg:px-6">
          <div className="max-w-7xl mx-auto">
            <div
              id="revamp-details"
              data-observe
              className={
                (visible["revamp-details"] ? "opacity-100 translate-y-0 animate-slideInUp" : "opacity-0 translate-y-20") +
                " transition-all duration-1000 flex flex-col lg:flex-row gap-8 lg:gap-12 items-center"
              }
              onMouseEnter={(e) => {
                setHoveredService(revampServiceIndex);
                setCardRect(e.currentTarget.getBoundingClientRect());
              }}
              onMouseLeave={() => {
                setHoveredService(null);
                setIsHoveringButton(false);
                setCardRect(null); 
                setCursorScale(1);
              }}
            >
                {/* Image Block (Left) */}
                <div className="w-full lg:w-1/2 relative order-1 lg:order-1">
                    <div className="relative w-full h-64 sm:h-80 lg:h-96 rounded-3xl overflow-hidden shadow-2xl border-4 lg:border-8 border-white/50 float-animation">
                        <img src="/web_revamp.jpg" alt={revampService.title} className="w-full h-full object-cover" />
                        <div className={`absolute inset-0 bg-gradient-to-br ${revampService.gradient} opacity-10`} />
                    </div>
                </div>
                
                {/* Text Block (Right) */}
                <div className="w-full lg:w-1/2 flex flex-col space-y-6 text-center lg:text-left lg:items-start p-2 order-2 lg:order-2">
                    <h3 className="text-2xl lg:text-4xl font-black text-slate-900 leading-tight">It's Time To Upgrade Your Digital Asset</h3>
                    <p className="text-slate-600 leading-relaxed text-sm lg:text-base">
                        {revampService.description} Our service goes beyond aesthetics; we inject modern UI/UX principles, boost load times, and improve user flows to ensure your refreshed website actively contributes to your bottom line.
                    </p>
                    
                    <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center w-full gap-6">
                        <ul className="space-y-3 w-full lg:w-auto">
                            {revampService.features.map((f, i) => (
                            <li key={i} className="flex items-center gap-3 justify-center lg:justify-start">
                                <div className={`w-2 h-2 bg-gradient-to-r ${revampService.gradient} rounded-full flex-shrink-0`} />
                                <span className="text-slate-700 font-medium text-sm lg:text-base">{f}</span>
                            </li>
                            ))}
                        </ul>

                        {/* CTA Button */}
                        <a 
  href="/consult"
  className="group px-6 py-3 bg-cyan-500 text-white font-bold rounded-xl shadow-lg 
             hover:bg-cyan-600 hover:scale-105 transition-all text-base lg:text-lg"
  onMouseEnter={() => setIsHoveringButton(true)}
  onMouseLeave={() => setIsHoveringButton(false)}
>
  <span className="flex items-center justify-center gap-2">
    Upgrade My Website
    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
  </span>
</a>

                    </div>
                </div>
            </div>
          </div>
        </section>

       

        <WhyChooseUsSection visible={visible} />
        
        <CTABannerSection visible={visible} />
        
        <FooterSection />
        </div>
      </main>
    </div>
    
  );
}